import { KeyboardEvent, useRef, useState } from "react";
import { Field } from "../Field";
import { Chip } from "../Chip";
import s from "./TagInput.module.scss";

export type TagInputProps = {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  hint?: string;
  error?: string;
  maxLength?: number;
  removeLabel: (value: string) => string;
};

/** Free-text tags: Enter or comma adds (trimmed, no blanks, no duplicates); Backspace on an empty field removes the last. */
export const TagInput = ({ label, values, onChange, placeholder, hint, error, maxLength, removeLabel }: TagInputProps) => {
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  /** Adds every piece in ONE onChange (trimmed, no blanks, no duplicates, also against existing values). */
  const addAll = (pieces: string[]) => {
    const next = [...values];
    pieces.forEach((raw) => { const v = raw.trim(); if (v && !next.includes(v)) next.push(v); });
    if (next.length !== values.length) onChange(next);
  };
  const add = (raw: string) => { setText(""); addAll([raw]); };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(text); }
    else if (e.key === "Backspace" && text === "" && values.length) onChange(values.slice(0, -1));
  };

  return (
    <Field label={label} hint={hint} error={error}>
      {({ id, describedBy, invalid }) => (
        <div className={s.box} onClick={() => inputRef.current?.focus()}>
          {values.map((v) => (
            <Chip key={v} variant="remove" label={<bdi>{v}</bdi>} removeLabel={removeLabel(v)}
              onRemove={() => { onChange(values.filter((x) => x !== v)); inputRef.current?.focus(); }} />
          ))}
          <input
            id={id} ref={inputRef} className={s.input} value={text} maxLength={maxLength} placeholder={placeholder} aria-describedby={describedBy} aria-invalid={invalid || undefined} autoComplete="off"
            onChange={(e) => {
              const v = e.target.value;
              if (!v.includes(",")) { setText(v); return; }
              const parts = v.split(",");
              setText(parts[parts.length - 1]);
              addAll(parts.slice(0, -1));
            }}
            onBlur={() => add(text)}
            onKeyDown={onKeyDown}
          />
        </div>
      )}
    </Field>
  );
};
